import { useState } from "react";
import { Plus, Layers } from "lucide-react";
import RoomCard from "@/components/RoomCard";
import { ROOM_TYPES, buildDefaultWorkItems } from "@/lib/roomTemplates";
import { roomTotals } from "@/lib/calculations";
import { useFormatCurrency } from "@/lib/CurrencyContext";
import { getGoal } from "@/lib/projectGoals";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import UpgradeModal from "@/components/UpgradeModal";

// SpaceCost projects are capped at 3 rooms by default; each PLAN_ROOM_PACK
// (R80 ZAR) purchase grants +3 more.
const SPACECOST_BASE_ROOM_CAP = 3;
const SPACECOST_ROOMS_PER_PACK = 3;
// FlipCost trial — capped at 1 room until the user upgrades. Surfaces an
// inline "Unlock for $19 / R280" CTA in place of the Add Room button.
const FLIPCOST_TRIAL_ROOM_CAP = 1;
function spaceCostRoomCap(project) {
  const packs = Number(project?.extra_room_packs || 0);
  return SPACECOST_BASE_ROOM_CAP + Math.max(0, packs) * SPACECOST_ROOMS_PER_PACK;
}

function newRoomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "r-" + Math.random().toString(36).slice(2);
}

export default function RoomsTab({ project, onRoomsChange, costLibrary = null }) {
  const rooms = project.rooms || [];
  const fmt = useFormatCurrency();
  const goal = getGoal(project?.project_goal);
  const [open, setOpen] = useState(false);
  const [roomPackOpen, setRoomPackOpen] = useState(false);
  const isSpaceCost = project?.product === "spacecost";
  const roomCap = isSpaceCost ? spaceCostRoomCap(project) : Infinity;
  const atRoomCap = isSpaceCost && rooms.length >= roomCap;
  // FlipCost trial: 1-room cap. Once they hit it, the Add Room button flips
  // to a sage "Unlock for X" CTA that opens Paystack via TrialBanner's flow.
  const isTrial = !isSpaceCost && Boolean(project?.trial);
  const atTrialCap = isTrial && rooms.length >= FLIPCOST_TRIAL_ROOM_CAP;
  const [form, setForm] = useState({
    name: "",
    room_type: "bathroom",
    size_sqm: 6,
    condition: "tired",
    renovation_scope: "moderate",
    finish_level: "mid",
  });

  const grandTotal = rooms.reduce((s, r) => s + roomTotals(r).total, 0);

  const createRoom = () => {
    if (atRoomCap) {
      setOpen(false);
      setRoomPackOpen(true);
      return;
    }
    if (atTrialCap) {
      // Trial users hitting the cap get bounced to Pricing — the dashboard
      // TrialBanner already has an unlock CTA but we mirror it here too.
      setOpen(false);
      window.location.href = "/pricing";
      return;
    }
    const items = buildDefaultWorkItems({
      room_type: form.room_type,
      size_sqm: form.size_sqm,
      renovation_scope: form.renovation_scope,
      finish_level: form.finish_level,
      condition: form.condition,
      overrides: costLibrary?.room_rates?.[form.room_type] || {},
      currency: project.currency || "USD",
    });
    const room = {
      id: newRoomId(),
      name: form.name || defaultNameFor(form.room_type, rooms),
      room_type: form.room_type,
      size_sqm: Number(form.size_sqm) || 0,
      condition: form.condition,
      renovation_scope: form.renovation_scope,
      finish_level: form.finish_level,
      work_items: items,
    };
    onRoomsChange([...rooms, room]);
    setOpen(false);
    setForm({ ...form, name: "" });
  };

  const updateRoom = (id, patch) => {
    onRoomsChange(rooms.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const deleteRoom = (id) => {
    onRoomsChange(rooms.filter((r) => r.id !== id));
  };

  const regenerateItems = (id) => {
    const target = rooms.find((r) => r.id === id);
    if (!target) return;
    const items = buildDefaultWorkItems({
      room_type: target.room_type,
      size_sqm: target.size_sqm,
      renovation_scope: target.renovation_scope,
      finish_level: target.finish_level,
      condition: target.condition,
      overrides: costLibrary?.room_rates?.[target.room_type] || {},
      currency: project.currency || "USD",
    });
    updateRoom(id, { work_items: items });
  };

  return (
    <div className="space-y-6 fc-fade-in" data-testid="rooms-tab">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h2 className="fc-heading text-xl text-[#1C1C1C]" data-testid="rooms-heading">{goal.rooms.heading}</h2>
          <p className="text-xs text-[#A8A29E] mt-1" data-testid="rooms-description">
            {goal.rooms.description}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="fc-label">Interior Total</div>
            <div className="fc-heading text-lg text-[#1C1C1C] mt-0.5" data-testid="rooms-grand-total">
              {fmt(grandTotal)}
            </div>
          </div>
          <Dialog open={open} onOpenChange={(v) => {
            // Block opening the Add Room dialog when SpaceCost is at room cap;
            // surface the +3 rooms upsell instead. FlipCost trial is gated
            // separately below via the button's onClick handler.
            if (v && atRoomCap) { setRoomPackOpen(true); return; }
            if (v && atTrialCap) { window.location.href = "/pricing"; return; }
            setOpen(v);
          }}>
            <DialogTrigger asChild>
              <Button
                data-testid={atTrialCap ? "add-room-trial-cap-btn" : "add-room-btn"}
                className={
                  atTrialCap
                    ? "bg-sage-700 text-white hover:bg-sage-800 rounded-lg h-9 px-3"
                    : "bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-9 px-3"
                }
              >
                <Plus className="w-4 h-4 mr-1.5" />
                {atTrialCap
                  ? "Unlock to add more rooms"
                  : atRoomCap
                  ? `Add Room (${rooms.length}/${roomCap})`
                  : "Add Room"}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-black/[0.08] text-[#27272A]">
              <DialogHeader>
                <DialogTitle className="fc-heading text-[#1C1C1C]">New Room</DialogTitle>
                <DialogDescription className="text-xs text-[#A8A29E]">
                  Upgrades auto-generate from type, size, scope and finish. Everything is editable.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-2">
                <Labeled label="Name">
                  <Input
                    data-testid="new-room-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Master Bathroom"
                    className="fc-input h-10"
                  />
                </Labeled>
                <Labeled label="Type">
                  <SelectWrap
                    tid="new-room-type-select"
                    value={form.room_type}
                    onChange={(v) => setForm({ ...form, room_type: v })}
                    options={ROOM_TYPES}
                  />
                </Labeled>
                <Labeled label="Size (sqm)">
                  <Input
                    data-testid="new-room-size-input"
                    type="number"
                    value={form.size_sqm}
                    onChange={(e) => setForm({ ...form, size_sqm: Number(e.target.value) })}
                    className="fc-input h-10"
                  />
                </Labeled>
                <Labeled label="Condition">
                  <SelectWrap
                    tid="new-room-condition-select"
                    value={form.condition}
                    onChange={(v) => setForm({ ...form, condition: v })}
                    options={[
                      { value: "good", label: "Good" },
                      { value: "tired", label: "Tired" },
                      { value: "poor", label: "Poor" },
                    ]}
                  />
                </Labeled>
                <Labeled label="Scope">
                  <SelectWrap
                    tid="new-room-scope-select"
                    value={form.renovation_scope}
                    onChange={(v) => setForm({ ...form, renovation_scope: v })}
                    options={[
                      { value: "none", label: "None" },
                      { value: "cosmetic", label: "Cosmetic" },
                      { value: "moderate", label: "Moderate" },
                      { value: "full", label: "Full" },
                    ]}
                  />
                </Labeled>
                <Labeled label="Finish">
                  <SelectWrap
                    tid="new-room-finish-select"
                    value={form.finish_level}
                    onChange={(v) => setForm({ ...form, finish_level: v })}
                    options={[
                      { value: "budget", label: "Budget" },
                      { value: "mid", label: "Mid" },
                      { value: "premium", label: "Premium" },
                    ]}
                  />
                </Labeled>
              </div>
              <DialogFooter>
                <Button
                  data-testid="confirm-add-room-btn"
                  onClick={createRoom}
                  className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E]"
                >
                  Create Room
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {rooms.length === 0 ? (
        <div className="fc-card fc-sage p-10 text-center" data-testid="rooms-empty-state">
          <div className="w-12 h-12 rounded-full bg-sage-100 border border-sage-200 grid place-items-center mx-auto">
            <Layers className="w-5 h-5 text-sage-700" />
          </div>
          <div className="fc-heading text-[#1C1C1C] mt-4">No rooms added</div>
          <div className="text-xs text-[#57534E] mt-1">
            Click "Add Room" to start planning a typical set of upgrades.
          </div>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 gap-4"
          data-testid="rooms-grid"
        >
          {rooms.map((room) => {
            const roomTotal = roomTotals(room).total;
            const pct = goal.rooms.perRoomShowPct
              ? (grandTotal > 0 ? (roomTotal / grandTotal) * 100 : 0)
              : null;
            return (
              <RoomCard
                key={room.id}
                room={room}
                onChange={(patch) => updateRoom(room.id, patch)}
                onDelete={() => deleteRoom(room.id)}
                onRegenerate={() => regenerateItems(room.id)}
                onAddRoom={() => atRoomCap ? setRoomPackOpen(true) : setOpen(true)}
                budgetPct={pct}
                costLibrary={costLibrary}
                currency={project.currency || "USD"}
              />
            );
          })}
        </div>
      )}

      {/* SpaceCost-only +3 rooms upsell modal. */}
      {isSpaceCost && (
        <UpgradeModal
          open={roomPackOpen}
          onOpenChange={setRoomPackOpen}
          product="spacecost"
          mode="room_pack"
          projectId={project.id}
          roomCap={roomCap}
        />
      )}
    </div>
  );
}

function defaultNameFor(type, existing) {
  const base = {
    bathroom: "Bathroom", kitchen: "Kitchen", bedroom: "Bedroom",
    lounge: "Lounge", dining: "Dining", laundry: "Laundry",
    hallway: "Hallway", office: "Office", other: "Room",
  }[type] || "Room";
  const count = existing.filter((r) => r.room_type === type).length;
  return count === 0 ? base : `${base} ${count + 1}`;
}

function Labeled({ label, children }) {
  return (
    <div>
      <div className="fc-label mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function SelectWrap({ tid, value, onChange, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={tid} className="fc-input h-10 rounded-md">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
